/**
 * Data mapper utilities for transforming between database and frontend formats
 */

/**
 * Map database event record to frontend format
 * @param {Object} dbEvent - Event from database
 * @returns {Object} Frontend event object
 */
export function mapEventFromDb(dbEvent) {
  return {
    id: dbEvent.id,
    name: dbEvent.name,
    date: new Date(dbEvent.date),
    presenter: dbEvent.presenter,
    supportStaff: dbEvent.event_staff?.map(staff => ({
      name: staff.name,
      role: staff.role || 'Coordinator',
      teamMemberId: staff.team_member_id || null
    })) || [],
    attendees: [],
    registrationUrl: dbEvent.registration_url,
    locationId: dbEvent.location_id,
    format: dbEvent.format || 'in_person',
    meetingUrl: dbEvent.meeting_url || '',
    capacity: dbEvent.capacity ?? null,
    campaignId: dbEvent.campaign_id,
    organizationId: dbEvent.organization_id,
    foodOption: dbEvent.food_option || 'none',
    capacityBuffer: dbEvent.capacity_buffer || 0,
    notes: dbEvent.notes || '',
    impactNotes: dbEvent.impact_notes || '',
    costLog: [],
    reminder3DaySentAt: dbEvent.reminder_3day_sent_at,
    reminder1DaySentAt: dbEvent.reminder_1day_sent_at,
    createdAt: dbEvent._created_at,
    createdBy: dbEvent._created_by
  };
}

/**
 * Map frontend event to database format for insert/update
 * @param {Object} event - Frontend event object
 * @param {string} organizationId - Organization ID
 * @returns {Object} Database event record
 */
export function mapEventToDb(event, organizationId) {
  return {
    name: event.name,
    date: event.date instanceof Date ? event.date.toISOString() : event.date,
    presenter: event.presenter,
    registration_url: event.registrationUrl,
    ...mapEventFormatToDb(event),
    campaign_id: event.campaignId,
    food_option: event.foodOption || 'none',
    capacity_buffer: event.capacityBuffer || 0,
    notes: event.notes || null,
    organization_id: organizationId
  };
}

/**
 * Map the in-person/virtual fields, clearing whichever side doesn't apply
 * so a converted event doesn't keep a stale venue or meeting link.
 * @param {Object} event - Frontend event object
 * @returns {Object} location_id, format, meeting_url, capacity
 */
export function mapEventFormatToDb(event) {
  const isVirtual = event.format === 'virtual';
  return {
    format: isVirtual ? 'virtual' : 'in_person',
    location_id: isVirtual ? null : (event.locationId || null),
    meeting_url: isVirtual ? (event.meetingUrl?.trim() || null) : null,
    capacity: isVirtual && event.capacity !== '' && event.capacity != null ? Number(event.capacity) : null
  };
}

/**
 * Map database attendee record to frontend format
 * @param {Object} dbAttendee - Attendee from database
 * @returns {Object} Frontend attendee object
 */
export function mapAttendeeFromDb(dbAttendee) {
  return {
    id: dbAttendee.id,
    firstName: dbAttendee.first_name,
    lastName: dbAttendee.last_name,
    name: `${dbAttendee.first_name} ${dbAttendee.last_name}`,
    email: dbAttendee.email,
    phoneNumber: dbAttendee.phone_number,
    streetAddress: dbAttendee.street_address,
    city: dbAttendee.city,
    state: dbAttendee.state,
    zipCode: dbAttendee.zip_code,
    notes: dbAttendee.notes || '',
    referralSource: dbAttendee.referral_source || '',
    gender: dbAttendee.gender || '',
    relationship: dbAttendee.relationship || '',
    hostId: dbAttendee.host_id,
    organizationId: dbAttendee.organization_id,
    eventIds: dbAttendee.event_attendees?.map(ea => ea.event_id) || [],
    eventAttendees: dbAttendee.event_attendees?.map(ea => ({
      eventId: ea.event_id,
      checkedIn: ea.checked_in,
      interestedInAppointment: ea.interested_in_appointment,
      confirmed: ea.confirmed,
      confirmationSource: ea.confirmation_source,
      status: ea.status || 'registered',
      waitlistPosition: ea.waitlist_position,
      allowSms: ea.notification_preferences?.sms || false,
      newsletter: ea.notification_preferences?.newsletter || false,
      revenue: ea.revenue != null ? Number(ea.revenue) : null,
      revenueDate: ea.revenue_date || null
    })) || []
  };
}

/**
 * Map frontend attendee to database format
 * @param {Object} attendee - Frontend attendee object
 * @param {string} organizationId - Organization ID
 * @returns {Object} Database attendee record
 */
export function mapAttendeeToDb(attendee, organizationId) {
  return {
    first_name: attendee.firstName,
    last_name: attendee.lastName,
    email: attendee.email || null,
    phone_number: attendee.phoneNumber || null,
    street_address: attendee.streetAddress,
    city: attendee.city,
    state: attendee.state,
    zip_code: attendee.zipCode,
    notes: attendee.notes,
    referral_source: attendee.referralSource,
    gender: attendee.gender,
    host_id: attendee.hostId,
    organization_id: organizationId
  };
}

/**
 * Map database campaign record to frontend format
 * @param {Object} dbCampaign - Campaign from database
 * @param {Array} costLogs - Cost logs for this campaign
 * @returns {Object} Frontend campaign object
 */
export function mapCampaignFromDb(dbCampaign, costLogs = []) {
  return {
    id: dbCampaign.id,
    name: dbCampaign.name,
    notes: dbCampaign.notes,
    startDate: new Date(dbCampaign.start_date),
    endDate: new Date(dbCampaign.end_date),
    budget: dbCampaign.budget,
    campaignNumber: dbCampaign.campaign_number,
    mailersSent: dbCampaign.mailers_sent,
    mailerSpend: dbCampaign.mailer_spend,
    presentationName: dbCampaign.presentation_name,
    officeId: dbCampaign.office_id || null,
    organizationId: dbCampaign.organization_id,
    costLog: costLogs
      .filter(log => log.campaign_id === dbCampaign.id)
      .map(log => ({
        id: log.id,
        amount: log.amount,
        description: log.description,
        date: new Date(log.date)
      }))
  };
}

/**
 * Map frontend campaign to database format
 * @param {Object} campaign - Frontend campaign object
 * @param {string} organizationId - Organization ID
 * @returns {Object} Database campaign record
 */
export function mapCampaignToDb(campaign, organizationId) {
  return {
    name: campaign.name,
    notes: campaign.notes,
    start_date: campaign.startDate instanceof Date ? campaign.startDate.toISOString() : campaign.startDate,
    end_date: campaign.endDate instanceof Date ? campaign.endDate.toISOString() : campaign.endDate,
    budget: campaign.budget || null,
    campaign_number: campaign.campaignNumber,
    mailers_sent: campaign.mailersSent || null,
    mailer_spend: campaign.mailerSpend || null,
    presentation_name: campaign.presentationName || null,
    office_id: campaign.officeId || null,
    organization_id: organizationId
  };
}

/**
 * Map database location record to frontend format
 * @param {Object} dbLocation - Location from database
 * @returns {Object} Frontend location object
 */
export function mapLocationFromDb(dbLocation) {
  return {
    id: dbLocation.id,
    name: dbLocation.name,
    address: dbLocation.address,
    city: dbLocation.city || '',
    state: dbLocation.state || '',
    contact: dbLocation.contact,
    phone: dbLocation.phone || '',
    notes: dbLocation.notes || '',
    capacity: dbLocation.capacity,
    cost: dbLocation.cost,
    zipCode: dbLocation.zip_code,
    amenities: convertAmenitiesToArray(dbLocation.amenities),
    drivingDirections: dbLocation.driving_directions || '',
    organizationId: dbLocation.organization_id,
    officeId: dbLocation.office_id || null
  };
}

/**
 * Map frontend location to database format
 * @param {Object} location - Frontend location object
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID
 * @returns {Object} Database location record
 */
export function mapLocationToDb(location, organizationId, userId) {
  return {
    name: location.name,
    address: location.address,
    city: location.city,
    state: location.state,
    contact: location.contact,
    phone: location.phone,
    notes: location.notes,
    capacity: location.capacity,
    cost: location.cost,
    zip_code: location.zipCode,
    amenities: convertAmenitiesToArray(location.amenities),
    driving_directions: location.drivingDirections || null,
    organization_id: organizationId,
    user_id: userId,
    office_id: location.officeId || null
  };
}

/**
 * Map event attendee from database format
 * @param {Object} record - Database event attendee with joined data
 * @returns {Object} Frontend event attendee object
 */
export function mapEventAttendeeFromDb(record) {
  // Safety check for null attendee (can happen if attendee was soft-deleted)
  if (!record.attendees) {
    console.error('mapEventAttendeeFromDb: record.attendees is null for event_attendee', record.id);
    return null;
  }
  
  return {
    id: record.attendees.id,
    eventAttendeeId: record.id,
    firstName: record.attendees.first_name,
    lastName: record.attendees.last_name,
    name: `${record.attendees.first_name} ${record.attendees.last_name}`,
    email: record.attendees.email,
    phoneNumber: record.attendees.phone_number,
    hostId: record.attendees.host_id,
    streetAddress: record.attendees.street_address,
    city: record.attendees.city,
    state: record.attendees.state,
    zipCode: record.attendees.zip_code,
    notes: record.attendees.notes,
    referralSource: record.attendees.referral_source,
    gender: record.attendees.gender || '',
    relationship: record.attendees.relationship || '',
    checkedIn: record.checked_in,
    checkedInAt: record.checked_in_at,
    checkedInBy: record.checked_in_by,
    interestedInAppointment: record.interested_in_appointment,
    interestedInAppointmentAt: record.interested_in_appointment_at,
    interestedInAppointmentBy: record.interested_in_appointment_by,
    confirmed: record.confirmed || false,
    confirmedAt: record.confirmed_at,
    confirmedBy: record.confirmed_by,
    confirmationSource: record.confirmation_source || undefined,
    status: record.status || 'registered',
    waitlistPosition: record.waitlist_position,
    allowSms: record.notification_preferences?.sms || false,
    newsletter: record.notification_preferences?.newsletter || false,
    evaluationCompleted: record.evaluation_completed || false,
    evaluationCompletedAt: record.evaluation_completed_at,
    evaluationCompletedBy: record.evaluation_completed_by,
    revenue: record.revenue != null ? Number(record.revenue) : null,
    revenueDate: record.revenue_date || null,
    registeredAt: record._created_at,
    registeredBy: record._created_by,
    // Filter out soft-deleted appointments
    appointments: (record.appointments || []).filter(apt => apt._deleted !== true)
  };
}

/**
 * Map organization user from database format
 * @param {Object} dbUser - User organization record from database
 * @returns {Object} Frontend user object
 */
export function mapOrganizationUserFromDb(dbUser) {
  return {
    userId: dbUser.user_id,
    email: dbUser.users?.email || dbUser.email,
    role: dbUser.role,
    invitedAt: dbUser.invited_at,
    invitedBy: dbUser.invited_by
  };
}

/**
 * Map database planning period record to frontend format
 * @param {Object} dbPeriod - Planning period from database
 * @param {Array} plannedEvents - Planned events for this period
 * @returns {Object} Frontend planning period object
 */
export function mapPlanningPeriodFromDb(dbPeriod, plannedEvents = []) {
  return {
    id: dbPeriod.id,
    name: dbPeriod.name,
    startDate: new Date(dbPeriod.start_date),
    endDate: new Date(dbPeriod.end_date),
    mailerCount: dbPeriod.mailer_count,
    mailerBudget: dbPeriod.mailer_budget,
    totalBudget: dbPeriod.total_budget,
    notes: dbPeriod.notes || '',
    status: dbPeriod.status || 'planning',
    campaignId: dbPeriod.campaign_id,
    campaignNumberOverride: dbPeriod.campaign_number_override || null,
    organizationId: dbPeriod.organization_id,
    createdAt: dbPeriod._created_at,
    createdBy: dbPeriod._created_by,
    plannedEvents: plannedEvents
      .filter(pe => pe.planning_period_id === dbPeriod.id)
      .map(mapPlannedEventFromDb)
  };
}

/**
 * Map frontend planning period to database format
 * @param {Object} period - Frontend planning period object
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID
 * @returns {Object} Database planning period record
 */
export function mapPlanningPeriodToDb(period, organizationId, userId) {
  return {
    name: period.name,
    start_date: period.startDate instanceof Date ? period.startDate.toISOString() : period.startDate,
    end_date: period.endDate instanceof Date ? period.endDate.toISOString() : period.endDate,
    mailer_count: period.mailerCount || null,
    mailer_budget: period.mailerBudget || null,
    total_budget: period.totalBudget || null,
    notes: period.notes || null,
    status: period.status || 'planning',
    campaign_id: period.campaignId || null,
    campaign_number_override: period.campaignNumberOverride || null,
    organization_id: organizationId,
    user_id: userId
  };
}

/**
 * Map database planned event record to frontend format
 * @param {Object} dbEvent - Planned event from database
 * @returns {Object} Frontend planned event object
 */
export function mapPlannedEventFromDb(dbEvent) {
  return {
    id: dbEvent.id,
    planningPeriodId: dbEvent.planning_period_id,
    tentativeDate: new Date(dbEvent.tentative_date),
    locationId: dbEvent.location_id,
    presenterId: dbEvent.presenter_id,
    coordinatorId: dbEvent.coordinator_id,
    officeId: dbEvent.office_id,
    foodOption: dbEvent.food_option || 'none',
    estimatedCost: dbEvent.estimated_cost,
    notes: dbEvent.notes || '',
    callNotes: dbEvent.call_notes || '',
    // Milestones
    venueCalled: dbEvent.venue_called || false,
    venueCalledAt: dbEvent.venue_called_at,
    venueCalledBy: dbEvent.venue_called_by,
    venueBooked: dbEvent.venue_booked || false,
    venueBookedAt: dbEvent.venue_booked_at,
    venueBookedBy: dbEvent.venue_booked_by,
    depositPaid: dbEvent.deposit_paid || false,
    depositPaidAt: dbEvent.deposit_paid_at,
    depositPaidBy: dbEvent.deposit_paid_by,
    mailersOrdered: dbEvent.mailers_ordered || false,
    mailersOrderedAt: dbEvent.mailers_ordered_at,
    mailersOrderedBy: dbEvent.mailers_ordered_by,
    // Link to created event
    eventId: dbEvent.event_id,
    organizationId: dbEvent.organization_id,
    createdAt: dbEvent._created_at,
    createdBy: dbEvent._created_by,
    // Joined data (if present)
    locationName: dbEvent.locations?.name || null,
    presenterName: dbEvent.presenters?.name || null,
    coordinatorName: dbEvent.office_team_members?.name || null,
    officeName: dbEvent.offices?.name || null
  };
}

/**
 * Map frontend planned event to database format
 * @param {Object} event - Frontend planned event object
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID
 * @returns {Object} Database planned event record
 */
export function mapPlannedEventToDb(event, organizationId, userId) {
  const dbData = {
    planning_period_id: event.planningPeriodId,
    tentative_date: event.tentativeDate instanceof Date ? event.tentativeDate.toISOString() : event.tentativeDate,
    location_id: event.locationId || null,
    presenter_id: event.presenterId || null,
    coordinator_id: event.coordinatorId || null,
    office_id: event.officeId || null,
    food_option: event.foodOption || 'none',
    estimated_cost: event.estimatedCost || null,
    notes: event.notes || null,
    call_notes: event.callNotes || null,
    organization_id: organizationId,
    user_id: userId
  };

  // Include milestones if provided (for updates)
  if (event.venueCalled !== undefined) {
    dbData.venue_called = event.venueCalled;
    if (event.venueCalled && !event.venueCalledAt) {
      dbData.venue_called_at = new Date().toISOString();
      dbData.venue_called_by = userId;
    } else if (!event.venueCalled) {
      dbData.venue_called_at = null;
      dbData.venue_called_by = null;
    }
  }
  if (event.venueBooked !== undefined) {
    dbData.venue_booked = event.venueBooked;
    if (event.venueBooked && !event.venueBookedAt) {
      dbData.venue_booked_at = new Date().toISOString();
      dbData.venue_booked_by = userId;
    } else if (!event.venueBooked) {
      dbData.venue_booked_at = null;
      dbData.venue_booked_by = null;
    }
  }
  if (event.depositPaid !== undefined) {
    dbData.deposit_paid = event.depositPaid;
    if (event.depositPaid && !event.depositPaidAt) {
      dbData.deposit_paid_at = new Date().toISOString();
      dbData.deposit_paid_by = userId;
    } else if (!event.depositPaid) {
      dbData.deposit_paid_at = null;
      dbData.deposit_paid_by = null;
    }
  }
  if (event.mailersOrdered !== undefined) {
    dbData.mailers_ordered = event.mailersOrdered;
    if (event.mailersOrdered && !event.mailersOrderedAt) {
      dbData.mailers_ordered_at = new Date().toISOString();
      dbData.mailers_ordered_by = userId;
    } else if (!event.mailersOrdered) {
      dbData.mailers_ordered_at = null;
      dbData.mailers_ordered_by = null;
    }
  }

  return dbData;
}

/**
 * Map database lead list record to frontend format
 */
export function mapLeadListFromDb(dbList) {
  return {
    id: dbList.id,
    organizationId: dbList.organization_id,
    name: dbList.name,
    provider: dbList.provider || '',
    orderNumber: dbList.order_number || '',
    importedAt: dbList.imported_at ? new Date(dbList.imported_at) : null,
    fileName: dbList.file_name || '',
    filePath: dbList.file_path || '',
    fieldMapping: dbList.field_mapping || {},
    locationId: dbList.location_id || null,
    locationName: dbList.locations?.name || null,
    recordCount: dbList.record_count || 0,
    notes: dbList.notes || '',
    status: dbList.status || 'active',
    ncoaProcessed: dbList.ncoa_processed || false,
    cassCertified: dbList.cass_certified || false,
    createdAt: dbList._created_at,
    createdBy: dbList._created_by
  };
}

/**
 * Map frontend lead list to database format
 */
export function mapLeadListToDb(list, organizationId) {
  return {
    organization_id: organizationId,
    name: list.name,
    provider: list.provider || null,
    order_number: list.orderNumber || null,
    imported_at: list.importedAt instanceof Date ? list.importedAt.toISOString() : list.importedAt || new Date().toISOString(),
    file_name: list.fileName || null,
    file_path: list.filePath || null,
    field_mapping: list.fieldMapping || {},
    location_id: list.locationId || null,
    record_count: list.recordCount || 0,
    notes: list.notes || null,
    status: list.status || 'active',
    ncoa_processed: list.ncoaProcessed || false,
    cass_certified: list.cassCertified || false
  };
}

/**
 * Map database lead record to frontend format
 */
export function mapLeadFromDb(dbLead) {
  return {
    id: dbLead.id,
    organizationId: dbLead.organization_id,
    leadNumber: dbLead.lead_number,
    externalLeadId: dbLead.external_lead_id || '',
    leadListId: dbLead.lead_list_id,
    firstName: dbLead.first_name || '',
    lastName: dbLead.last_name || '',
    name: `${dbLead.first_name || ''} ${dbLead.last_name || ''}`.trim(),
    email: dbLead.email || '',
    phone: dbLead.phone || '',
    streetAddress: dbLead.street_address || '',
    city: dbLead.city || '',
    state: dbLead.state || '',
    zipCode: dbLead.zip_code || '',
    plus4: dbLead.plus4 || '',
    age: dbLead.age || '',
    income: dbLead.income || '',
    ipa: dbLead.ipa || '',
    netWorth: dbLead.net_worth || '',
    gender: dbLead.gender || '',
    maritalStatus: dbLead.marital_status || '',
    lengthOfResidence: dbLead.length_of_residence || '',
    locationId: dbLead.location_id || null,
    rawData: dbLead.raw_data || {},
    status: dbLead.status || 'active',
    doNotContact: dbLead.do_not_contact || false,
    doNotContactReason: dbLead.do_not_contact_reason || '',
    saleDate: dbLead.sale_date || null,
    attendeeId: dbLead.attendee_id || null,
    createdAt: dbLead._created_at,
    createdBy: dbLead._created_by
  };
}

/**
 * Map frontend lead to database format
 */
export function mapLeadToDb(lead, organizationId) {
  return {
    organization_id: organizationId,
    lead_number: lead.leadNumber,
    external_lead_id: lead.externalLeadId || null,
    lead_list_id: lead.leadListId,
    first_name: lead.firstName || null,
    last_name: lead.lastName || null,
    email: lead.email || null,
    phone: lead.phone || null,
    street_address: lead.streetAddress || null,
    city: lead.city || null,
    state: lead.state || null,
    zip_code: lead.zipCode || null,
    plus4: lead.plus4 || null,
    age: lead.age || null,
    income: lead.income || null,
    ipa: lead.ipa || null,
    net_worth: lead.netWorth || null,
    gender: lead.gender || null,
    marital_status: lead.maritalStatus || null,
    length_of_residence: lead.lengthOfResidence || null,
    location_id: lead.locationId || null,
    raw_data: lead.rawData || {},
    status: lead.status || 'active',
    do_not_contact: lead.doNotContact || false,
    do_not_contact_reason: lead.doNotContactReason || null,
    sale_date: lead.saleDate || null,
    attendee_id: lead.attendeeId || null
  };
}

/**
 * Map database lead contact record to frontend format
 */
export function mapLeadContactFromDb(dbContact) {
  return {
    id: dbContact.id,
    organizationId: dbContact.organization_id,
    leadId: dbContact.lead_id,
    campaignId: dbContact.campaign_id || null,
    channel: dbContact.channel,
    contactDate: dbContact.contact_date,
    notes: dbContact.notes || '',
    createdAt: dbContact._created_at,
    createdBy: dbContact._created_by
  };
}

/**
 * Map frontend lead contact to database format
 */
export function mapLeadContactToDb(contact, organizationId) {
  return {
    organization_id: organizationId,
    lead_id: contact.leadId,
    campaign_id: contact.campaignId || null,
    channel: contact.channel,
    contact_date: contact.contactDate,
    notes: contact.notes || null
  };
}

/**
 * Map database campaign production record to frontend format
 */
export function mapCampaignProductionFromDb(dbProd) {
  return {
    id: dbProd.id,
    organizationId: dbProd.organization_id,
    planningPeriodId: dbProd.planning_period_id || null,
    campaignId: dbProd.campaign_id || null,
    leadListId: dbProd.lead_list_id || null,
    productionType: dbProd.production_type || 'mailer',
    quantity: dbProd.quantity || null,
    vendor: dbProd.vendor || '',
    notes: dbProd.notes || '',
    // Milestones
    leadsOrdered: dbProd.leads_ordered || false,
    leadsOrderedAt: dbProd.leads_ordered_at,
    leadsOrderedBy: dbProd.leads_ordered_by,
    leadsReceived: dbProd.leads_received || false,
    leadsReceivedAt: dbProd.leads_received_at,
    leadsReceivedBy: dbProd.leads_received_by,
    artworkUploaded: dbProd.artwork_uploaded || false,
    artworkUploadedAt: dbProd.artwork_uploaded_at,
    artworkUploadedBy: dbProd.artwork_uploaded_by,
    artworkFilePath: dbProd.artwork_file_path || '',
    artworkApproved: dbProd.artwork_approved || false,
    artworkApprovedAt: dbProd.artwork_approved_at,
    artworkApprovedBy: dbProd.artwork_approved_by,
    sentToPrint: dbProd.sent_to_print || false,
    sentToPrintAt: dbProd.sent_to_print_at,
    sentToPrintBy: dbProd.sent_to_print_by,
    printConfirmed: dbProd.print_confirmed || false,
    printConfirmedAt: dbProd.print_confirmed_at,
    printConfirmedBy: dbProd.print_confirmed_by,
    mailDropScheduled: dbProd.mail_drop_scheduled || null,
    mailDropped: dbProd.mail_dropped || false,
    mailDroppedAt: dbProd.mail_dropped_at,
    mailDroppedBy: dbProd.mail_dropped_by,
    inHomesDate: dbProd.in_homes_date || null,
    createdAt: dbProd._created_at,
    createdBy: dbProd._created_by,
    // Joined data
    leadListName: dbProd.lead_lists?.name || null,
    planningPeriodName: dbProd.planning_periods?.name || null,
    campaignName: dbProd.campaigns?.name || null
  };
}

/**
 * Map frontend campaign production to database format
 */
export function mapCampaignProductionToDb(prod, organizationId) {
  return {
    organization_id: organizationId,
    planning_period_id: prod.planningPeriodId || null,
    campaign_id: prod.campaignId || null,
    lead_list_id: prod.leadListId || null,
    production_type: prod.productionType || 'mailer',
    quantity: prod.quantity || null,
    vendor: prod.vendor || null,
    notes: prod.notes || null,
    mail_drop_scheduled: prod.mailDropScheduled || null,
    in_homes_date: prod.inHomesDate || null
  };
}


/**
 * Map campaign_leads junction row (with joined lead data) from database format
 */
export function mapCampaignLeadFromDb(dbRow) {
  const lead = dbRow.leads || {};
  const leadList = lead.lead_lists || dbRow.lead_lists || {};
  return {
    id: dbRow.id,
    campaignId: dbRow.campaign_id,
    leadId: dbRow.lead_id,
    organizationId: dbRow.organization_id,
    status: dbRow.status || 'selected',
    sortScore: dbRow.sort_score,
    channel: dbRow.channel || 'mail',
    distanceMiles: dbRow.distance_miles != null ? Number(dbRow.distance_miles) : null,
    notes: dbRow.notes || '',
    createdAt: dbRow._created_at,
    firstName: lead.first_name || '',
    lastName: lead.last_name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    city: lead.city || '',
    state: lead.state || '',
    zipCode: lead.zip_code || '',
    leadNumber: lead.lead_number || '',
    leadListName: leadList.name || '',
    leadListId: lead.lead_list_id || null,
    doNotContact: lead.do_not_contact || false,
    leadStatus: lead.status || 'active'
  };
}

/**
 * Map frontend campaign lead to database format for insert
 */
export function mapCampaignLeadToDb(record, organizationId) {
  return {
    campaign_id: record.campaignId,
    lead_id: record.leadId,
    organization_id: organizationId,
    status: record.status || 'selected',
    sort_score: record.sortScore || null,
    notes: record.notes || null
  };
}


// Helper function for amenities conversion
function convertAmenitiesToArray(amenities) {
  if (!amenities) return [];
  if (Array.isArray(amenities)) return amenities;
  if (typeof amenities === 'object') {
    const keys = Object.keys(amenities);
    return keys.length > 0 ? keys : [];
  }
  return [];
}
